// src/redux/slices/lottery.ts  ✅ PATCH TYPES TO MATCH PAGINATED API

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from '../../utils/axios';
import { AppThunk } from '../store.types';

export interface TchalaItem {
  id: number;
  label: string;
  numbers: string[];
}
export interface TirageItem {
  id: number;
  code: string;
  name: string;
  open_time: string;
  end_time: string;
  active: boolean;
}
export interface ResultItem {
  id: number;
  date: string;
  ball_1: string;
  ball_2: string;
  ball_3: string;
  tirage: {
    id: number;
    code: string;
    name: string;
    open_time: string;
    end_time: string;
    active: boolean;
  };
}

export interface PageResp<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

interface LotteryState {
  isLoading: boolean;
  error: string | null;

  tchala: PageResp<TchalaItem>;
  tchalaPage: number;
  tchalaPageSize: number;
  tchalaSearch: string;

  tirages: PageResp<TirageItem>; // ← paginated
  results: ResultItem[]; // ← array (non paginé)
  latest: ResultItem[];
}

const initialState: LotteryState = {
  isLoading: false,
  error: null,

  tchala: { count: 0, next: null, previous: null, results: [] },
  tchalaPage: 1,
  tchalaPageSize: 20,
  tchalaSearch: '',

  tirages: { count: 0, next: null, previous: null, results: [] }, // ← paginated shape
  results: [],
  latest: [],
};

const slice = createSlice({
  name: 'lottery',
  initialState,
  reducers: {
    startLoading(s) {
      s.isLoading = true;
      s.error = null;
    },
    hasError(s, a: PayloadAction<string>) {
      s.isLoading = false;
      s.error = a.payload;
    },

    setTchalaParams(s, a: PayloadAction<{ page?: number; page_size?: number; search?: string }>) {
      if (a.payload.page !== undefined) s.tchalaPage = a.payload.page;
      if (a.payload.page_size !== undefined) s.tchalaPageSize = a.payload.page_size;
      if (a.payload.search !== undefined) s.tchalaSearch = a.payload.search;
    },
    getTchalaSuccess(s, a: PayloadAction<PageResp<TchalaItem>>) {
      s.isLoading = false;
      s.tchala = a.payload;
    },

    getTiragesSuccess(s, a: PayloadAction<PageResp<TirageItem>>) {
      // ← paginated
      s.isLoading = false;
      s.tirages = a.payload;
    },

    getResultsSuccess(s, a: PayloadAction<ResultItem[]>) {
      s.isLoading = false;
      s.results = a.payload;
    },
    getLatestResultsSuccess(s, a: PayloadAction<ResultItem[]>) {
      s.isLoading = false;
      s.latest = a.payload;
    },
  },
});

export default slice.reducer;
export const {
  startLoading,
  hasError,
  setTchalaParams,
  getTchalaSuccess,
  getTiragesSuccess,
  getResultsSuccess,
  getLatestResultsSuccess,
} = slice.actions;

// THUNKS
export const fetchTchala =
  (params?: { page?: number; page_size?: number; search?: string }): AppThunk =>
  async (dispatch, getState) => {
    dispatch(startLoading());
    try {
      const { tchalaPage, tchalaPageSize, tchalaSearch } = (getState() as any)
        .lottery as LotteryState;
      const page = params?.page ?? tchalaPage;
      const page_size = params?.page_size ?? tchalaPageSize;
      const search = params?.search ?? tchalaSearch;
      dispatch(setTchalaParams({ page, page_size, search }));
      const resp = await axios.get<PageResp<TchalaItem>>('/api/v1/lottery/tchala/', {
        params: { page, page_size, search },
      });
      dispatch(getTchalaSuccess(resp.data));
    } catch (e: any) {
      dispatch(hasError(e?.message || 'Tchala load failed'));
    }
  };

export const fetchTirages =
  (active = true, params?: { page?: number; page_size?: number }): AppThunk =>
  async (dispatch) => {
    dispatch(startLoading());
    try {
      const resp = await axios.get<PageResp<TirageItem>>('/api/v1/lottery/tirages/', {
        params: { ...(active ? { active: 1 } : {}), ...(params || {}) },
      });
      dispatch(getTiragesSuccess(resp.data));
    } catch (e: any) {
      dispatch(hasError(e?.message || 'Tirages load failed'));
    }
  };

export const fetchResultsByDate =
  (date: string, code?: string): AppThunk =>
  async (dispatch) => {
    dispatch(startLoading());
    try {
      const resp = await axios.get<ResultItem[]>('/api/v1/lottery/results/', {
        params: { date, ...(code ? { code } : {}) },
      });
      dispatch(getResultsSuccess(resp.data));
    } catch (e: any) {
      dispatch(hasError(e?.message || 'Results load failed'));
    }
  };

